import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import paths from './paths';

// Retired payment entry points stay routed so old bookmarks and links land on
// the current payment options flow instead of a 404.
const RedirectToPaymentOptions = () => <Navigate to={paths.paymentPlan} replace />;

const HomePage = lazy(() => import(/* webpackChunkName: "page-home" */ '../pages/HomePage'));
const LoginPage = lazy(() => import(/* webpackChunkName: "page-login" */ '../pages/LoginPage'));
const VersionPage = lazy(() => import(/* webpackChunkName: "page-version" */ '../pages/VersionPage'));
const CookiebotPreviewPage = lazy(() =>
  import(/* webpackChunkName: "page-cookiebot-preview" */ '../pages/CookiebotPreviewPage')
);
const NotFoundPage = lazy(() => import(/* webpackChunkName: "page-not-found" */ '../pages/NotFoundPage'));
const SelectAccountPage = lazy(() =>
  import(/* webpackChunkName: "page-select-account" */ '../pages/SelectAccountPage')
);
const AccountErrorPage = lazy(() =>
  import(/* webpackChunkName: "page-account-error" */ '../pages/AccountErrorPage')
);
const AccountBackgroundPage = lazy(() =>
  import(/* webpackChunkName: "page-account-background" */ '../pages/AccountBackgroundPage')
);
const DocumentsPage = lazy(() => import(/* webpackChunkName: "page-documents" */ '../pages/DocumentsPage'));
const PaymentHistoryPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-history" */ '../pages/PaymentHistoryPage')
);
const AccountSettingsPage = lazy(() =>
  import(/* webpackChunkName: "page-account-settings" */ '../pages/AccountSettingsPage')
);
const DisclosuresPage = lazy(() =>
  import(/* webpackChunkName: "page-disclosures" */ '../pages/DisclosuresPage')
);
const PaymentPlanPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-plan" */ '../pages/PaymentPlanPage')
);
const PaymentPlanSetupPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-plan-setup" */ '../pages/PaymentPlanSetupPage')
);
const PaymentPlanErrorPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-plan-error" */ '../pages/PaymentPlanErrorPage')
);
const ShortLinkErrorPage = lazy(() =>
  import(/* webpackChunkName: "page-short-link-error" */ '../pages/ShortLinkErrorPage')
);
const SettlementOfferLandingPage = lazy(() =>
  import(/* webpackChunkName: "page-settlement-offer" */ '../pages/SettlementOfferLandingPage')
);
const PaymentPlanOfferLandingPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-plan-offer" */ '../pages/PaymentPlanOfferLandingPage')
);
const PaymentConfirmPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-confirm" */ '../pages/PaymentConfirmPage')
);
const PaymentSuccessPage = lazy(() =>
  import(/* webpackChunkName: "page-payment-success" */ '../pages/PaymentSuccessPage')
);
const SpecialOfferErrorPage = lazy(() =>
  import(/* webpackChunkName: "page-special-offer-error" */ '../pages/SpecialOfferErrorPage')
);
const FrictionlessLandingPage = lazy(() =>
  import(/* webpackChunkName: "page-frictionless" */ '../pages/FrictionlessLandingPage')
);
const ShortLinkRedirectPage = lazy(() =>
  import(/* webpackChunkName: "page-short-link" */ '../pages/ShortLinkRedirectPage')
);

// these will probably be evaluated in order, so for paths with params to be
// routed correctly, keep specificity in mind when ordering routes here

export default [
  // top level page routes
  {
    path: paths.home,
    component: HomePage,
    requireAuth: true,
  },
  {
    path: paths.login,
    component: LoginPage,
    hideNav: true,
  },
  {
    path: paths.version,
    component: VersionPage,
  },
  {
    path: paths.cookiebotPreview,
    component: CookiebotPreviewPage,
    hideNav: true,
  },
  {
    path: paths.notFound,
    component: NotFoundPage,
    requireAuth: true,
  },
  {
    path: '*', // no specificity issue here, '*' is special
    component: NotFoundPage,
    requireAuth: true,
  },

  // account page routes
  {
    path: paths.selectAccount,
    component: SelectAccountPage,
    hideNav: true,
    requireAuth: true,
  },
  {
    path: paths.accountError,
    component: AccountErrorPage,
  },
  {
    path: paths.accountBackground,
    component: AccountBackgroundPage,
    requireAuth: true,
  },
  {
    path: paths.documents,
    component: DocumentsPage,
    requireAuth: true,
  },
  {
    path: paths.paymentHistory,
    component: PaymentHistoryPage,
    requireAuth: true,
  },
  {
    path: paths.accountSettings,
    component: AccountSettingsPage,
    requireAuth: true,
  },

  // disclosures page routes
  {
    path: paths.disclosures,
    component: DisclosuresPage,
    requireAuth: false,
  },
  {
    path: paths.homeDisclosures,
    component: DisclosuresPage,
    requireAuth: true,
    hideNav: true,
  },
  {
    path: paths.uniqueDisclosures,
    component: DisclosuresPage,
    requireAuth: false,
    hideNav: true,
  },

  // payment plan page routes
  {
    path: paths.paymentPlan,
    component: PaymentPlanPage,
    requireAuth: true,
  },
  {
    path: paths.legacyPaymentPartial,
    component: RedirectToPaymentOptions,
    requireAuth: true,
  },
  {
    path: paths.paymentPlanSetup,
    component: PaymentPlanSetupPage,
    requireAuth: true,
  },
  {
    path: paths.legacyPaymentPlanCreate,
    component: RedirectToPaymentOptions,
    requireAuth: true,
  },
  {
    path: paths.paymentPlanError,
    component: PaymentPlanErrorPage,
  },
  {
    path: paths.shortLinkError,
    component: ShortLinkErrorPage,
    hideNav: true,
  },
  {
    path: paths.paymentConfirm,
    component: PaymentConfirmPage,
  },
  {
    path: paths.paymentSuccess,
    component: PaymentSuccessPage,
    // Standalone receipt (match offer landings: no side nav; page supplies its own chrome)
    hideNav: true,
  },

  // special offer page routes
  {
    path: paths.settlementOfferLandingPage,
    component: SettlementOfferLandingPage,
    hideNav: true,
  },
  {
    path: paths.paymentPlanOfferLandingPage,
    component: PaymentPlanOfferLandingPage,
    hideNav: true,
  },
  {
    path: paths.specialOfferError,
    component: SpecialOfferErrorPage,
    hideNav: true,
  },
  {
    path: paths.frictionlessLandingPage,
    component: FrictionlessLandingPage,
    hideNav: true,
  },

  // shortlink goes last so the param matching doesn't clobber other rules
  {
    path: paths.shortLink,
    component: ShortLinkRedirectPage,
    hideNav: true,
  },
];
